import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import GridLayout from "react-grid-layout";

import BuilderRightSideBar from "../../../Layouts/BuilderRightSideBar";
import Button from "../../../components/FormComponents/Button";
import { useAppBlocksContext } from "../../../context/AppBlocksContext";
import { useTableContext } from "../../../context/TableContext";
import RecordsViewEditSection from "../RecordsViewRenderer/Desktop/RecordsViewEditSection";
import PageInfoAndActionBar from "../../../Layouts/PageInfoAndActionBar";
import useElementDimensions from "../../../hooks/useElementDimensions";
import ButtonActionIcon from "../../../components/BuilderComponents/ButtonActionIcon";
import EditPivotChart from "./EditPivotChart";
import MaterialIcon from "../../../components/BuilderComponents/MaterialIcon";
import { generateObjectId } from "../../../utils/objectIdUtils";
import { toastError } from "../../../utils/toastUtils";

const getConfig = (id) => {
  const config = {
    id: id,
    presets: null,
    title: "",
    is_draggable: false
  };

  return config;
};

const PivotChartRenderer = () => {
  const tableRef = useRef({});
  const { getTableTypeData } = useTableContext();
  const navigate = useNavigate();
  const {
    layout,
    setLayout,
    layoutData,
    recordsviewLayout,
    setRecordsviewLayout,
    setSelectedAppBlock,
    applicationBlockData
  } = useAppBlocksContext();
  const [{ width: elementWidth }, elementRef] = useElementDimensions();

  const [pivotChartConfig, setPivotChartConfig] = useState(layoutData);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [dragFromOutside, setDragFromOutside] = useState(false);
  const [showPresetMenuFor, setShowPresetMenuFor] = useState();
  const [nonSelectedFilter, setNonSelectedFilter] = useState([]);

  const onDrop = (elemParams, item, e) => {
    setDragFromOutside(false);
    let element = e.dataTransfer.getData("type");
    if (!item || !item.i) {
      return;
    }
    if (!element || element === "null") return;

    const elementObject = getConfig(item.i);

    layoutData.current[item.i] = elementObject;

    setLayout([item, ...layout]);
  };

  const handleDragStart = (e, type) => {
    setDragFromOutside(true);
    if (e.nativeEvent instanceof DragEvent) {
      e.dataTransfer.setData("type", type);
    }
  };

  const fetchSingleTable = async () => {
    try {
      tableRef.current = {};
      if (!applicationBlockData.config.layout.table_id) return;
      let { tableObject } = await getTableTypeData(
        applicationBlockData.table_type,
        applicationBlockData.config.layout.table_id
      );

      tableRef.current = tableObject;
    } catch (error) {
      toastError(error.message);
      navigate("../");
      setSelectedAppBlock({ id: "", name: "", parentData: "", table_id: "", activity_id: "" });
    }
  };

  const afterEffectOfFilter = (data) => {
    const colIds = data.columns.map((obj) => obj.id);
    const richFields = returnRichFields(data);
    const richFieldIds = richFields.map((obj) => obj.id);
    const mainArrayKeys = new Set([...colIds, ...richFieldIds]);

    let selectedFilterArray = [];
    data.onscreen_filters.forEach((obj) => {
      if (mainArrayKeys.has(obj.id)) {
        selectedFilterArray.push({ ...obj, ...tableRef.current[obj.id] });
      }
    });

    const nonSelectedFilterArray = data.columns.filter(
      (obj) => !selectedFilterArray.some((selObj) => selObj.id === obj.id) && obj.type !== "custom"
    );

    const nonSelectedRichFields = richFields.filter(
      (field) => !data.onscreen_filters.some((filter) => filter.id === field.id)
    );
    const modifiedNonSelectedFilter = [...nonSelectedFilterArray, ...nonSelectedRichFields];

    let obj = {
      onscreen_filters: selectedFilterArray
    };

    setRecordsviewLayout({ ...data, ...obj });
    setNonSelectedFilter(modifiedNonSelectedFilter);
  };

  const returnRichFields = (data) => {
    const richFields = data.columns.flatMap(({ type, custom_type, layout_config }) => {
      if (type === "custom" && custom_type === "rich_column" && layout_config && Array.isArray(layout_config.layouts)) {
        return layout_config.layouts.flatMap(({ element_config }) =>
          element_config && element_config.type === "field" ? [element_config] : []
        );
      }
      return [];
    });

    return richFields;
  };

  const handleLayoutChange = (list) => {
    if (dragFromOutside) return;
    setLayout(list);
  };

  const initialDataFeetching = async () => {
    await fetchSingleTable();
    afterEffectOfFilter(recordsviewLayout);
  };

  const editPresetsCallback = (type, data) => {
    layoutData.current[showPresetMenuFor][type] = data;
    setLayout([...layout]);
    setPivotChartConfig({ ...layoutData });
  };

  const onDeleteBtnHandler = (id) => {
    const newArr = layout.filter((item) => item.i !== id);
    setLayout(newArr);
  };

  useEffect(() => {
    initialDataFeetching();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationBlockData.id]);

  return (
    <div className="chart_outer_body" style={{ gridTemplateColumns: "5fr auto" }}>
      <div>
        <PivotchartPageActionsHandler />
        <div className="chart_droppable_container" style={{ borderRadius: "0px", width: "100%", overflow: "auto" }}>
          <Button onClick={() => setIsEditOpen(true)}>Edit</Button>

          <div className="chart_droppable_inside">
            <div ref={elementRef}></div>
            <GridLayout
              rowHeight={110}
              cols={12}
              width={elementWidth}
              layout={layout}
              onDrop={(elemParams, item, e) => onDrop(elemParams, item, e)}
              compactType={"vertical"}
              isDroppable={true}
              droppingItem={{ i: generateObjectId(), h: 4, w: 12 }}
              useCSSTransforms={true}
              onLayoutChange={handleLayoutChange}
              className="draggable_elements_outer"
            >
              {layout.map((item) => (
                <div key={item.i} className="draggable_chart_fields">
                  <div className="builder_display_item_container">
                    {pivotChartConfig?.current[item.i]?.presets?.map((preset) => (
                      <div key={preset.id} className="builder_display_items" style={{ width: "fit-content" }}>
                        {preset.name}
                      </div>
                    ))}
                  </div>

                  <div
                    className="chart_view"
                    style={{
                      display: "flex",
                      width: "100%",
                      height: "100%",
                      overflow: "hidden",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    <div className="edit_Del_buttons">
                      <ButtonActionIcon type="edit" size="sm" onClick={() => setShowPresetMenuFor(item.i)} />
                      <ButtonActionIcon type="delete" size="sm" onClick={() => onDeleteBtnHandler(item.i)} />
                    </div>

                    <div className="overlay_cover"></div>

                    <img
                      src={require("../../../images/pivotChart.png")}
                      alt=""
                      style={{
                        width: "800px",
                        height: "400px"
                      }}
                    />
                  </div>
                </div>
              ))}
            </GridLayout>
          </div>
        </div>
      </div>

      <div className="rightsidebar">
        <div className="sidebar_top_header">Chart</div>

        <div className="right_sidebar_inner">
          <div draggable={true} onDragStart={(e) => handleDragStart(e, "type")} className="draggable_input_field">
            <MaterialIcon name="table" />
            <span className="field_name">Pivot Chart</span>
          </div>
        </div>
      </div>

      <BuilderRightSideBar
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        heading="Edit Pivot Chart"
        style={{ width: "710px" }}
      >
        <RecordsViewEditSection
          afterEffectOfFilter={afterEffectOfFilter}
          tableRef={tableRef}
          nonSelectedFilter={nonSelectedFilter}
          setNonSelectedFilter={setNonSelectedFilter}
        />
      </BuilderRightSideBar>

      <BuilderRightSideBar
        isOpen={showPresetMenuFor}
        onClose={() => setShowPresetMenuFor(undefined)}
        heading="Edit Pivot Chart"
        size="large"
      >
        <EditPivotChart
          callback={editPresetsCallback}
          selectedColumns={recordsviewLayout.columns}
          existingPresets={layoutData.current[showPresetMenuFor]?.presets}
          isDraggable={pivotChartConfig.current[showPresetMenuFor]?.is_draggable}
        />
      </BuilderRightSideBar>
    </div>
  );
};

const PivotchartPageActionsHandler = () => {
  const { recordsviewLayout, applicationBlockData, handleCloneApplicationBlock, handleSaveApplicationBlock } =
    useAppBlocksContext();
  return (
    <PageInfoAndActionBar
      pagePath={`Blocks/Pivotchart/${applicationBlockData?.name}`}
      clonePage={handleCloneApplicationBlock}
      savePage={handleSaveApplicationBlock}
      currentConfig={[recordsviewLayout, applicationBlockData]}
      pageName="applicationblock"
    />
  );
};

export default PivotChartRenderer;

export { PivotchartPageActionsHandler };
